import styled from 'styled-components';
import {
  Div,
  ImagePlaceholderIcon,
  TrashIconRed,
  UploadIcon,
} from '@vgrubs/components';

const UploadedImageStyled = styled.div<{
  $shape?: 'square' | 'circle';
  $width?: string;
  $height?: string;
  $heightSM?: string;
}>`
  width: ${({ $width }) => $width};
  position: relative;
  /* cursor: pointer; */
  display: inline-flex;
  flex-flow: column wrap;
  background-color: ${({ theme }) => theme.white};
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.lightGray}00;
  overflow: hidden;
  transition: ${({ theme }) => theme.transitions.ease300ms};
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $shape }) =>
    $shape === 'circle' && 'border-radius: 50%; aspect-ratio: 1/1 !important;'}
  img {
    height: ${({ $height }) => $height || '200px'};
    max-width: 100%;
    object-fit: contain;
    transition: ${({ theme }) => theme.transitions.ease300ms};
  }

  /* The small-screen height, applied whether or not the pointer is over the
     image. It used to live inside the :hover block below, which meant a
     caller could pass heightSM and see it only while hovering - i.e. never on
     the touch screens it was for. Also applies to the placeholder, so an
     empty input is the same size as a filled one. */
  @media (max-width: 800px) {
    img,
    > span > div {
      height: ${({ $heightSM, $height }) => $heightSM || $height || '200px'};
    }
  }

  :hover {
    background-color: ${({ theme }) => theme.white};
    border: 1px solid ${({ theme }) => theme.primary};
    > div {
      visibility: visible;
      opacity: 1;
      scale: 1;
    }

    > span > div {
      visibility: hidden;
      opacity: 0;
      scale: 0;
    }

    img {
      filter: opacity(25%);
    }
  }

  /* SVG */

  > div {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    visibility: hidden;
    opacity: 0;
    transition: ${({ theme }) => theme.transitions.ease300ms};
    transform-origin: 0 0;
    scale: 0;
  }

  > span > div {
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${({ $height }) => $height || '200px'};
    transition: ${({ theme }) => theme.transitions.ease300ms};
  }

  /* SVG */
`;

export const UploadedImage = ({
  onClick,
  image,
  PlaceholderImage,
  onDelete,
  noChange,
  shape = 'square',
  width = '100%',
  height = '200px',
  heightSM,
}: {
  onClick?: () => void;
  image: string | File | Blob | null;
  PlaceholderImage?: React.ElementType;
  onDelete?: () => void;
  noChange?: boolean;
  shape?: 'square' | 'circle';
  width?: string;
  height?: string;
  heightSM?: string;
}) => {
  const imageUrl =
    typeof image === 'string'
      ? image
      : image !== null
      ? URL.createObjectURL(image as File | Blob)
      : undefined;

  /*
   * The badge's home is the top-right of a 200px SQUARE. A circle clips its own
   * corners, so those coordinates put half the button behind the mask - and the
   * half that survived sat on the darkened photo with nothing separating it.
   *
   * Percentages keep it on the diagonal at any diameter; 14% is far enough in
   * that the whole button stays inside the curve. The white ring lifts it off
   * whatever the picture happens to be underneath.
   */
  const deleteStyle: React.CSSProperties =
    shape === 'circle'
      ? {
          right: '12%',
          top: '12%',
          left: 'auto',
          transform: 'none',
          borderRadius: '50%',
          boxShadow: '0 0 0 3px #fff',
          lineHeight: 0,
        }
      : { right: '0px', top: '30px', left: 'auto' };

  return (
    <UploadedImageStyled
      $shape={shape}
      $width={width}
      $height={height}
      $heightSM={heightSM}
    >
      {image && <img src={imageUrl} alt="" />}

      {!noChange && (
        <Div $pointer onClick={onClick}>
          <UploadIcon />
        </Div>
      )}

      {image && onDelete && (
        <Div $pointer style={deleteStyle} onClick={onDelete}>
          <TrashIconRed />
        </Div>
      )}

      {!image && (
        <span>
          <div onClick={onClick}>
            {PlaceholderImage ? <PlaceholderImage /> : <ImagePlaceholderIcon />}
          </div>
        </span>
      )}
    </UploadedImageStyled>
  );
};
